import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Area } from 'react-easy-crop';
import { tripsApi } from '../../../api/trips.api';
import { apiErrorCode } from '../../../api/client';
import { toast } from '../../ui/Toast';
import Modal from '../../ui/Modal';
import { cropToJpeg, loadImage } from '../../../utils/cropImage';
import { tooLargeMessage } from '../../../utils/uploadLimits';
import type { Trip } from '../../../types';

const ImageCropModal = lazy(() => import('../../ui/ImageCropModal'));

const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif';
// ADR-0032: the cover is framed 3:2 before it goes up.
const COVER_ASPECT = 3 / 2;
const COVER_WIDTH = 1800;
const COVER_HEIGHT = 1200;

type CoverFields = Pick<Trip, 'coverImageUrl' | 'coverImagePhotographer' | 'coverImagePhotographerUrl'>;

/**
 * Opened from a trip card on the dashboard, next to the story: upload a cover
 * of your own, or go back to the automatic one. Only for owners and editors.
 */
export default function TripCoverModal({ trip, onClose, onSaved }: {
  trip: Trip;
  onClose: () => void;
  onSaved: (cover: CoverFields) => void;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => () => { if (cropSrc) URL.revokeObjectURL(cropSrc); }, [cropSrc]);

  // An Unsplash cover carries its photographer; one of ours never does.
  const isOwnCover = !!trip.coverImageUrl && !trip.coverImagePhotographer;

  function failureMessage(err: unknown): string {
    const code = apiErrorCode(err);
    return code === 'UNSUPPORTED_FILE_TYPE' || code === 'IMAGE_DECODE_FAILED'
      ? t('trip.cover.unsupported')
      : t('trip.cover.failed');
  }

  async function upload(image: Blob, filename: string) {
    setSaving(true);
    try {
      const { coverImageUrl } = await tripsApi.uploadCover(trip.id, image, filename);
      toast(t('trip.cover.saved'), 'success');
      setCropSrc(null);
      onSaved({ coverImageUrl, coverImagePhotographer: null, coverImagePhotographerUrl: null });
    } catch (err) {
      toast(failureMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleFile(file: File) {
    const tooLarge = tooLargeMessage(file, 'media');
    if (tooLarge) { toast(tooLarge, 'error'); return; }

    const src = URL.createObjectURL(file);
    try {
      await loadImage(src);
      setCropSrc(src);
    } catch {
      // Only Safari decodes HEIC; the server centres the 3:2 itself.
      URL.revokeObjectURL(src);
      await upload(file, file.name);
    }
  }

  async function handleCrop(area: Area) {
    if (!cropSrc) return;
    try {
      await upload(await cropToJpeg(cropSrc, area, COVER_WIDTH, COVER_HEIGHT), 'cover.jpg');
    } catch {
      toast(t('trip.cover.failed'), 'error');
    }
  }

  async function handleRemove() {
    setSaving(true);
    try {
      await tripsApi.removeCover(trip.id);
      toast(t('trip.cover.removed'), 'success');
      onSaved({ coverImageUrl: null, coverImagePhotographer: null, coverImagePhotographerUrl: null });
    } catch {
      toast(t('trip.cover.failed'), 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={t('trip.cover.title')}>
      <div className="w-full aspect-[3/2] rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800">
        {trip.coverImageUrl && (
          <img src={trip.coverImageUrl} alt={t('trip.cover.alt')} className="w-full h-full object-cover" />
        )}
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mt-3">{t('trip.cover.hint')}</p>

      <div className="flex flex-wrap items-center gap-3 mt-4">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={saving}
          className="btn-primary"
        >
          {t('trip.cover.add')}
        </button>
        {isOwnCover && (
          <button
            type="button"
            onClick={handleRemove}
            disabled={saving}
            className="text-sm text-slate-500 hover:text-red-500 dark:text-slate-400 cursor-pointer"
          >
            {t('trip.cover.remove')}
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={e => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void handleFile(file);
        }}
      />
      {cropSrc && (
        <Suspense fallback={null}>
          <ImageCropModal
            src={cropSrc} saving={saving} aspect={COVER_ASPECT}
            title={t('trip.cover.cropTitle')} saveLabel={t('trip.cover.save')}
            onCancel={() => setCropSrc(null)} onConfirm={handleCrop}
          />
        </Suspense>
      )}
    </Modal>
  );
}
